import React, {useEffect, useState} from 'react';
import {COLORS, FONTS, SIZES} from '@fupa/fupa-uikit';
import styled, {css} from 'styled-components';
import {changeNavBar, updateHistoryMap} from 'app/actions/navbar';
import {useDispatch, useSelector} from 'react-redux';
import {NAVBAR_HOME, NAVBAR_MATCHES, NAVBAR_COMPETITIONS, NAVBAR_SEARCH} from 'app/reducers/navbar';
import {LinkOnComponent} from 'app/components/links/link';
import {createSelector} from 'reselect';
import {FuPaPlayer} from 'app/components/icons';
import {EmojiEvents, Event, Home, Search} from '@mui/icons-material';
import {useHistory} from 'react-router';
import {useEffectAfterMount} from 'app/hooks/dataHooks';
import {Badge} from '@mui/material';
import {styled as muiStyled} from '@mui/material/styles';
import {api} from 'app/services/api';
import {selectDistrict} from 'app/hooks/reduxCreateSelectorHooks';

const StyledBadge = muiStyled(Badge)(() => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    border: `1px solid ${COLORS.lightestGrey}`,
    padding: '0 4px',
  },
}));

const Container = styled.nav`
  transform: translate3d(0, 0, 0);
  -webkit-transform: translate3d(0, 0, 0);
  position: fixed;
  z-index: ${props => props.theme.zIndexNavigation};
  display: flex;
  justify-content: center;
  min-height: ${SIZES['64']};
  overflow: hidden;
  bottom: 0;
  width: 100vw;
  background-color: ${COLORS.lightestGrey};
  box-shadow: 0 -1px 3px 0 ${COLORS.grey};
  padding: ${SIZES['10']} 0;
  padding-bottom: calc(${SIZES['10']} + env(safe-area-inset-bottom));

  ${props => props.theme.desktopLayout`
    all: initial;
    display: flex;
    justify-content: center;
  `}
`;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  max-width: ${props => props.theme.desktopContentWidth}px;
`;

const Circle = styled.div`
  display: flex;
  border-radius: 100%;
  width: ${SIZES['28']};
  height: ${SIZES['28']};
  background-color: ${COLORS.lighterGrey};
  justify-content: center;
  align-items: center;
  ${props => props.theme.desktopLayout`
    width: ${SIZES['48']};
    height: ${SIZES['48']};
    background-color: rgba(256, 256, 256, 0.1);
  `}
`;

const Label = styled.span`
  font-family: ${FONTS.PTSans};
  font-size: ${FONTS['12']};
  ${props => props.theme.desktopLayout`
  color: ${COLORS.white};
  align-items: center;
  justify-content: center;
  position: absolute;
  padding-top: 1rem;
  `};
`;

const StyledFuPaPlayer = styled(FuPaPlayer)`
  &&& {
    padding-left: ${SIZES[2]};
    ${props => props.theme.desktopLayout`
      display: none;
    `}
    path:last-child {
      fill: ${props => (props.selected ? COLORS.white : COLORS.darkerGrey)};
    }
  }
`;

const StyledHome = styled(Home)`
  &&& {
    display: none;
    ${props => props.theme.desktopLayout`
      display: flex;
      padding-bottom: 0.75rem;
    `}
  }
`;

const StyledEvent = styled(Event)`
  &&& {
    ${props => props.theme.desktopLayout`
    fill: ${props.selected ? COLORS.fupaSecondary : COLORS.lighterGrey};
    padding-bottom: 0.75rem;
    `}
  }
`;

const StyledEmojiEvents = styled(EmojiEvents)`
  &&& {
    ${props => props.theme.desktopLayout`
    fill: ${props.selected ? COLORS.fupaSecondary : COLORS.lighterGrey};
    padding-bottom: 0.75rem;
    `}
  }
`;

const StyledSearch = styled(Search)`
  &&& {
    ${props => props.theme.desktopLayout`
    fill: ${props.selected ? COLORS.fupaSecondary : COLORS.lighterGrey};
    padding-bottom: 0.75rem;
    `}
  }
`;

const Tab = styled(LinkOnComponent)`
  display: flex;
  flex: 1 20%;
  height: ${SIZES['44']};
  align-items: center;
  flex-direction: column;
  color: ${COLORS.darkerGrey};
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(180, 180, 180, 0.1);
  ${props =>
    !props.selected &&
    css`
      :hover,
      :focus {
        background-color: ${COLORS.lighterGrey};
        ${Circle} {
          background-color: ${COLORS.lightGrey};
        }
      }
    `}

  ${props =>
    props.selected &&
    css`
      color: ${COLORS.fupaSecondary};
      ${Circle} {
        background-color: ${COLORS.fupaPrimary};
      }
    `}

  // Desktop Styles
  ${props => props.theme.desktopLayout`
    color: initial;
    justify-content: center;
    height: ${SIZES['48']};
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    margin: 0 0.375rem;

    ${props =>
      !props.selected &&
      css`
        :hover,
        :focus {
          background-color: transparent;
          ${Circle} {
            background-color: ${COLORS.fupaSecondaryFocused};
          }
          ${Label} {
            display: flex;
          }
        }
      `};

    ${props =>
      props.selected &&
      css`
        ${Circle} {
          background-color: ${COLORS.lighterGrey};
        }
        ${Label} {
          color: ${COLORS.fupaSecondary};
        }
      `};
  `}
`;

const Line = styled.div`
  display: none;
  ${props => props.theme.desktopLayout`
    display: block;
    box-sizing: border-box;
    height: 2.5rem;
    border: 1px solid ${COLORS.fupaPrimaryFocused};
    margin-left: 0.625rem;
  `}
`;

const selectNavBar = createSelector(
  state => state.navBar,
  navBar => {
    return {
      activeTab: navBar.activeTab,
      defaultUrl: navBar.defaultUrl,
      historyMap: navBar.historyMap,
    };
  }
);

const NavBar = ({className}) => {
  const dispatch = useDispatch();
  const district = useSelector(selectDistrict);
  const {activeTab, defaultUrl, historyMap} = useSelector(selectNavBar);
  const history = useHistory();
  const [showLiveBadge, setShowLiveBadge] = useState(false);
  const isRegion = /^\/region\//.test(history.location.pathname);
  const tabComponent = isRegion ? LinkOnComponent : 'div';

  const change = tabIndex => {
    dispatch(changeNavBar(tabIndex));
    if (!isRegion) {
      history.push(defaultUrl[tabIndex]);
    }
  };

  useEffect(() => {
    if (!district.slug) {
      return;
    }

    api
      .get(`/districts/${district.slug}/live-status`)
      .then(response => {
        setShowLiveBadge(!!response.data?.live);
      })
      .catch(() => {
        setShowLiveBadge(false);
      });
  }, [district.slug]);

  // Set active tab on back-navigation
  useEffectAfterMount(() => {
    const {action, location} = history;
    if (action === 'PUSH' || action === 'REPLACE') {
      dispatch(updateHistoryMap({key: location.key, tabIndex: activeTab}));
    } else {
      dispatch(changeNavBar(historyMap[location.key]));
    }
  }, [history.location.pathname, activeTab]);

  return (
    <Container className={className}>
      <Wrapper>
        <Tab
          selected={activeTab === NAVBAR_HOME}
          to={defaultUrl[NAVBAR_HOME]}
          onClick={() => change(NAVBAR_HOME)}
          as={tabComponent}>
          <Circle>
            <StyledFuPaPlayer
              selected={activeTab === NAVBAR_HOME}
              style={{color: activeTab === NAVBAR_HOME ? COLORS.fupaSecondary : COLORS.darkerGrey, fontSize: 20}}
            />
            <StyledHome
              selected={activeTab === NAVBAR_HOME}
              style={{color: activeTab === NAVBAR_HOME ? COLORS.fupaSecondary : COLORS.lighterGrey, fontSize: 20}}
            />
          </Circle>
          <Label>Home</Label>
        </Tab>
        <Tab
          selected={activeTab === NAVBAR_MATCHES}
          to={defaultUrl[NAVBAR_MATCHES]}
          onClick={() => change(NAVBAR_MATCHES)}
          as={tabComponent}>
          <Circle>
            <StyledBadge badgeContent={'live'} color='secondary' invisible={!showLiveBadge}>
              <StyledEvent
                selected={activeTab === NAVBAR_MATCHES}
                style={{color: activeTab === NAVBAR_MATCHES ? COLORS.fupaSecondary : COLORS.darkerGrey, fontSize: 20}}
              />
            </StyledBadge>
          </Circle>
          <Label>Spiele</Label>
        </Tab>
        <Tab
          selected={activeTab === NAVBAR_COMPETITIONS}
          to={defaultUrl[NAVBAR_COMPETITIONS]}
          onClick={() => change(NAVBAR_COMPETITIONS)}
          as={tabComponent}>
          <Circle>
            <StyledEmojiEvents
              selected={activeTab === NAVBAR_COMPETITIONS}
              style={{
                color: activeTab === NAVBAR_COMPETITIONS ? COLORS.fupaSecondary : COLORS.darkerGrey,
                fontSize: 20,
              }}
            />
          </Circle>
          <Label>Ligen</Label>
        </Tab>
        <Tab
          selected={activeTab === NAVBAR_SEARCH}
          to={defaultUrl[NAVBAR_SEARCH]}
          onClick={() => change(NAVBAR_SEARCH)}
          as={tabComponent}>
          <Circle>
            <StyledSearch
              selected={activeTab === NAVBAR_SEARCH}
              style={{color: activeTab === NAVBAR_SEARCH ? COLORS.fupaSecondary : COLORS.darkerGrey, fontSize: 20}}
            />
          </Circle>
          <Label>Suche</Label>
        </Tab>
      </Wrapper>
      <Line />
    </Container>
  );
};

export {NavBar};
